import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Tabs, Typogaphy } from 'vnetwork-ui'

import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { paths } from '@/utils/path'
import useTabs from '@/hooks/useTabs'
import PeopleIcons from '@/icons/PeopleIcons'
import { Briefcase } from 'lucide-react'

import { getDepartmentById } from '../../services'
import PositionsTab from './PositionsTab'
import EmployeesTab from './EmployeesTab'
import { useModal } from '@/contexts/modal'
import { PositionCreateModalProps } from '@/modals/PositionModals/Create'
import AddIcons from '@/icons/AddIcons'

const DepartmentDetail = () => {
    const { id } = useParams<{ id: string }>()
    const { t } = useTranslation('employee')
    const departmentId = Number(id)

    // Fetch department details
    const { data: departmentData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DEPARTMENTS, departmentId],
        queryFn: () => getDepartmentById(departmentId),
        enabled: !!departmentId,
    })

    const department = departmentData?.result

    // Tabs configuration - must include component property
    const tabs = useMemo(
        () => [
            {
                label: t('employee.positions'),
                value: 'positions',
                startIcon: <Briefcase className="w-4 h-4" />,
                component: <PositionsTab departmentId={departmentId} />,
            },
            {
                label: t('employees'),
                value: 'employees',
                startIcon: <PeopleIcons className="w-4 h-4" />,
                component: <EmployeesTab departmentId={departmentId} />,
            },
        ],
        [t, departmentId]
    )

    const { TabComponent, TabProps } = useTabs({
        tabs,
        clearFilters: true,
    })

    const { setModal } = useModal()
    // Handle create
    const handleCreate = useCallback(() => {
        setModal<PositionCreateModalProps>('PositionModals/Create/index', {
            departmentId,
        })
    }, [setModal, departmentId])

    const stateComponent = renderState()

    if (stateComponent) {
        return stateComponent
    }

    return (
        <div className="">
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.employeesManage,
                        title: t('employee_management'),
                    },
                    {
                        path: `${paths.employeesManage}?tab=departments`,
                        title: t('department_list'),
                    },
                    {
                        path: '#',
                        title:
                            department?.department_name ||
                            t('department_detail'),
                    },
                ]}
                actions={[
                    {
                        icon: <AddIcons className="w-4 h-4" />,
                        label: t('create_position.title'),
                        onClick: () => handleCreate(),
                    },
                ]}
            />

            {/* Department Info Card */}
            <Paper className="p-4 mt-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <Typogaphy className="text-xs text-gray-500">
                            {t('department_name')}
                        </Typogaphy>
                        <Typogaphy className="font-medium">
                            {department?.department_name}
                        </Typogaphy>
                    </div>
                    <div>
                        <Typogaphy className="text-xs text-gray-500">
                            {t('department_code')}
                        </Typogaphy>
                        <Typogaphy className="font-medium">
                            {department?.department_code || '-'}
                        </Typogaphy>
                    </div>
                    <div>
                        <Typogaphy className="text-xs text-gray-500">
                            {t('department_leader')}
                        </Typogaphy>
                        <Typogaphy className="font-medium">
                            {department?.department_leader_name || '-'}
                        </Typogaphy>
                    </div>
                    <div>
                        <Typogaphy className="text-xs text-gray-500">
                            {t('status')}
                        </Typogaphy>
                        <span
                            className={`inline-block px-2 py-1 rounded-full text-[10px] ${
                                department?.department_status === 'active'
                                    ? 'bg-green-100 text-c-success'
                                    : 'bg-gray-100 text-gray-700'
                            }`}
                        >
                            {department?.department_status === 'active'
                                ? t('status_active')
                                : t('status_inactive')}
                        </span>
                    </div>
                    <div className="md:col-span-4">
                        <Typogaphy className="text-xs text-gray-500">
                            {t('description')}
                        </Typogaphy>
                        <Typogaphy className="font-medium">
                            {department?.department_description || '-'}
                        </Typogaphy>
                    </div>
                </div>
            </Paper>

            {/* Tabs */}
            <div className="space-y-2">
                <Tabs {...TabProps} />
                {TabComponent}
            </div>
        </div>
    )
}

export default DepartmentDetail
